import styles from './SpendCharts.module.css';

/** Animated line-chart placeholder (matches TrendLineChart footprint). */
export function SpendTrendChartSkeleton({ label = 'Loading chart…' }: { label?: string }) {
  return (
    <div
      className={styles.chartSkeletonWrap}
      role="status"
      aria-busy="true"
      aria-label={label}
    >
      <div className={styles.chartSkeletonSvg}>
        <svg className={styles.chartSkeletonWave} viewBox="0 0 400 120" aria-hidden>
          <defs>
            <linearGradient id="skGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#dfe6e9" />
              <stop offset="50%" stopColor="#b2bec3" />
              <stop offset="100%" stopColor="#dfe6e9" />
            </linearGradient>
          </defs>
          {[0, 1, 2, 3].map((i) => (
            <line
              key={i}
              x1="24"
              y1={22 + i * 28}
              x2="376"
              y2={22 + i * 28}
              stroke="#ecf0f1"
              strokeWidth="1"
            />
          ))}
          <path
            d="M 40 88 Q 120 24 200 56 T 360 40"
            fill="none"
            stroke="url(#skGrad)"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray="12 8"
            className={styles.chartSkeletonPath}
          />
          <circle cx="40" cy="88" r="4" fill="#bdc3c7" opacity="0.8" />
          <circle cx="200" cy="56" r="4" fill="#bdc3c7" opacity="0.8" />
          <circle cx="360" cy="40" r="4" fill="#bdc3c7" opacity="0.8" />
        </svg>
      </div>
      <div className={styles.chartSkeletonLegend}>
        <span className={`${styles.skeletonShimmer} ${styles.legendSkeletonBar}`} />
        <span className={`${styles.skeletonShimmer} ${styles.legendSkeletonBar}`} />
      </div>
      <p className={styles.loadingChartLabel}>{label}</p>
    </div>
  );
}

function SkeletonBars({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <table className={styles.skeletonTable}>
      <tbody>
        {Array.from({ length: rows }).map((_, ri) => (
          <tr key={ri}>
            {Array.from({ length: cols }).map((_, ci) => (
              <td key={ci}>
                <span
                  className={`${styles.skeletonShimmer} ${styles.skeletonBar} ${
                    ci === 0 ? styles.skeletonBarLg : styles.skeletonBarSm
                  } ${ci > 0 ? styles.skeletonRowNum : ''}`}
                  style={{ marginLeft: ci > 0 ? 'auto' : undefined, display: 'block' }}
                />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Mini-table placeholder for impact / variance sections. */
export function SpendMiniTableSkeleton({
  rows = 6,
  cols = 5,
  caption,
}: {
  rows?: number;
  cols?: number;
  caption?: string;
}) {
  return (
    <div role="status" aria-busy="true" aria-label={caption ?? 'Loading table'}>
      {caption ? <p className={styles.loadingChartLabel}>{caption}</p> : null}
      <SkeletonBars rows={rows} cols={cols} />
    </div>
  );
}

/** Baseline line + table grid (use under real Executive Summary headings while loading). */
export function ExecutiveSummarySkeleton() {
  return (
    <div className={styles.execSkeletonWrap} role="status" aria-busy="true" aria-label="Loading executive summary">
      <div className={`${styles.skeletonShimmer} ${styles.execSummarySkeletonBaseline}`} />
      <div style={{ overflowX: 'auto' }}>
        <table className={styles.miniTable}>
          <thead>
            <tr>
              {Array.from({ length: 9 }).map((_, i) => (
                <th key={i} className={styles.numeric}>
                  <span
                    className={`${styles.skeletonShimmer} ${styles.skeletonBar}`}
                    style={{ width: i === 0 ? 56 : 44, height: 11, display: 'inline-block' }}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[0, 1, 2].map((i) => (
              <tr key={i}>
                {Array.from({ length: 9 }).map((_, j) => (
                  <td key={j} className={styles.numeric}>
                    <span
                      className={`${styles.skeletonShimmer} ${styles.skeletonBar} ${styles.skeletonBarSm}`}
                      style={{ marginLeft: 'auto', display: 'block' }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** One category block: header bar + table skeleton. */
export function SpendCategoryBlockSkeleton() {
  return (
    <div className={styles.catBlock}>
      <div className={styles.catHead}>
        <span className={`${styles.skeletonShimmer} ${styles.skeletonBar}`} style={{ width: 160, height: 14 }} />
        <span className={`${styles.skeletonShimmer} ${styles.skeletonBar}`} style={{ width: 200, height: 36 }} />
      </div>
      <div style={{ padding: 12 }}>
        <SpendMiniTableSkeleton rows={5} cols={7} />
      </div>
    </div>
  );
}

/** Variance section: supplier grid + category drill-down blocks. */
export function SpendVarianceSkeleton({
  caption = 'Loading supplier variance…',
  ariaLabel = 'Loading variance data',
}: {
  caption?: string;
  ariaLabel?: string;
} = {}) {
  return (
    <div role="status" aria-busy="true" aria-label={ariaLabel}>
      <SpendMiniTableSkeleton rows={5} cols={8} caption={caption} />
      <div style={{ marginTop: 16 }}>
        <SpendCategoryBlockSkeleton />
        <SpendCategoryBlockSkeleton />
      </div>
    </div>
  );
}

/** Model impact card area: callouts + trend skeleton. */
export function ModelImpactCardSkeleton({ index = 0 }: { index?: number }) {
  return (
    <div className={styles.impactBlockSkeleton}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, gap: 12 }}>
        <span className={`${styles.skeletonShimmer} ${styles.skeletonBar}`} style={{ width: 180, height: 18 }} />
        <span className={`${styles.skeletonShimmer} ${styles.skeletonBar}`} style={{ width: 120, height: 24 }} />
      </div>
      <div className={styles.calloutRow}>
        <div className={styles.callout}>
          <span className={`${styles.skeletonShimmer} ${styles.skeletonBar}`} style={{ width: '80%', height: 12 }} />
          <span
            className={`${styles.skeletonShimmer} ${styles.skeletonBar}`}
            style={{ width: '50%', height: 28, marginTop: 10 }}
          />
        </div>
        <div className={styles.callout}>
          <span className={`${styles.skeletonShimmer} ${styles.skeletonBar}`} style={{ width: '80%', height: 12 }} />
          <span
            className={`${styles.skeletonShimmer} ${styles.skeletonBar}`}
            style={{ width: '40%', height: 28, marginTop: 10 }}
          />
        </div>
      </div>
      <SpendTrendChartSkeleton label={index === 0 ? 'Rendering chart…' : undefined} />
    </div>
  );
}
